/**
 * UltraPass loopback control-plane client.
 *
 * The Windows authenticator service exposes a WebSocket control plane on localhost. This module
 * speaks the one route the browser SDK needs — `/payload` — plus a reachability check.
 *
 * ## Why this exists
 *
 * On Windows 11 24H2/25H2 the authenticator can register as a *plugin authenticator*: a COM object
 * Windows' own WebAuthn stack calls, rather than an emulated CTAP2 USB-HID device. That transport
 * carries a ceremony as exactly one CTAP command, and the platform does not forward the
 * `largeBlob` or `prf`/`hmac-secret` extensions across it — measured on 25H2 26200.8875, with
 * `credProtect` as the positive control that crossed in the same request.
 *
 * So on that transport `largeBlob: { read: true }` comes back empty no matter what the RP asks
 * for. The JWE is still minted during the assertion; it just has no route home. `/payload` is that
 * route: the page names the assertion it holds and the service hands the payload over.
 *
 * @module core/loopback
 */

/**
 * Default loopback control-plane origin.
 *
 * Externalized because the port is the authenticator service's choice, not a protocol
 * constant.
 */
export const DEFAULT_LOOPBACK_URL = 'ws://127.0.0.1:47213';

/**
 * Options for a loopback request.
 */
export interface LoopbackOptions {
  /**
   * Control-plane origin, without a path.
   *
   * @default DEFAULT_LOOPBACK_URL
   */
  url?: string;

  /**
   * How long to wait for the socket to open and answer, in milliseconds.
   *
   * A refused port fails fast, but a firewalled one can hang — the cap keeps the fallback from
   * outlasting the ceremony it is meant to rescue.
   *
   * @default 3000
   */
  timeout?: number;

  /**
   * Whether to log progress.
   */
  debug?: boolean;
}

/**
 * Result of a loopback reachability check.
 */
export interface LoopbackReachability {
  reachable: boolean;
  detail: string;
}

/**
 * Sends one JSON frame to a loopback route and resolves with the parsed reply.
 *
 * The control plane is request/response per connection, so this opens, sends, reads one frame,
 * and closes.
 *
 * @param path - Route path, e.g. `/payload`
 * @param request - JSON-serializable request frame
 * @param options - Connection options
 * @returns The parsed reply, or null if the socket could not be reached or did not answer in time
 */
function sendLoopbackFrame(
  path: string,
  request: unknown,
  options: LoopbackOptions = {}
): Promise<Record<string, unknown> | null> {
  const base = options.url || DEFAULT_LOOPBACK_URL;
  const timeout = options.timeout ?? 3000;

  return new Promise((resolve) => {
    if (typeof WebSocket === 'undefined') {
      resolve(null);
      return;
    }

    let socket: WebSocket | undefined;
    let settled = false;

    const done = (result: Record<string, unknown> | null, why?: string) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (socket) {
        try {
          socket.close();
        } catch {
          // Closing is best-effort; the reply is already in hand.
        }
      }
      if (why && options.debug) {
        console.log(`[FIDO2-JS] loopback ${path}: ${why}`);
      }
      resolve(result);
    };

    const timer = setTimeout(() => done(null, `no reply within ${timeout}ms`), timeout);

    try {
      socket = new WebSocket(base + path);
    } catch (error: any) {
      done(null, `could not open socket: ${error?.message}`);
      return;
    }

    socket.onopen = () => {
      try {
        socket!.send(JSON.stringify(request));
      } catch (error: any) {
        done(null, `send failed: ${error?.message}`);
      }
    };

    socket.onmessage = (event: MessageEvent) => {
      try {
        const parsed = JSON.parse(String(event.data));
        done(parsed && typeof parsed === 'object' ? parsed : null, 'reply received');
      } catch (error: any) {
        done(null, `reply was not JSON: ${error?.message}`);
      }
    };

    // The browser deliberately withholds the reason from script, so all of these look identical
    // here: connection refused, an Origin the service rejected, and — the one that catches people
    // out — the browser refusing to let a PUBLIC page origin reach loopback at all.
    //
    // That last case is the common one in development. A page served from an https tunnel
    // (trycloudflare, ngrok) is a public origin, and browsers restrict public → private/loopback
    // requests. The authenticator can be running perfectly and still be unreachable from such a
    // page. Serving the page from http://localhost on the same machine removes the restriction.
    socket.onerror = () =>
      done(
        null,
        'could not connect — service not running, Origin rejected, or the browser blocked a ' +
          'public page origin from reaching loopback'
      );
  });
}

/**
 * Checks whether the UltraPass loopback control plane is listening.
 *
 * Useful as a diagnostic: a missing authenticator and a refusing one both surface as
 * `NotAllowedError` from WebAuthn, which cannot tell them apart.
 *
 * Opens `/payload` and deliberately sends nothing — the routes that would do work either start a
 * face capture (`/faceauth`) or consume a one-shot payload.
 *
 * @param options - Connection options
 * @returns Whether the socket opened, with a human-readable detail
 */
export function checkLoopbackReachable(
  options: LoopbackOptions = {}
): Promise<LoopbackReachability> {
  const base = options.url || DEFAULT_LOOPBACK_URL;
  const timeout = options.timeout ?? 3000;

  return new Promise((resolve) => {
    if (typeof WebSocket === 'undefined') {
      resolve({ reachable: false, detail: 'no WebSocket in this environment' });
      return;
    }

    let socket: WebSocket | undefined;
    let settled = false;

    const done = (result: LoopbackReachability) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (socket) {
        try {
          socket.close();
        } catch {
          // best-effort
        }
      }
      resolve(result);
    };

    const timer = setTimeout(
      () => done({ reachable: false, detail: `no response within ${timeout}ms (firewalled?)` }),
      timeout
    );

    try {
      socket = new WebSocket(base + '/payload');
    } catch (error: any) {
      done({ reachable: false, detail: `WebSocket constructor threw: ${error?.message}` });
      return;
    }

    socket.onopen = () => done({ reachable: true, detail: 'socket opened' });
    socket.onerror = () => done({ reachable: false, detail: 'connection refused or blocked' });
  });
}

/**
 * Collects the JWE payload minted during an assertion that could not return it via `largeBlob`.
 *
 * The base64 `clientDataJSON` both names the assertion and authorizes the handover — only the page
 * that ran the ceremony holds it, and the service hashes it to match. Base64url is accepted: the
 * service normalizes `-`/`_` itself. The payload is **one-shot** and expires 120 seconds after
 * minting, so this must be called promptly and its result reused rather than re-fetched.
 *
 * @param clientDataJSON - Base64 (or base64url) `assertion.response.clientDataJSON`
 * @param options - Connection options
 * @returns The payload as a JSON string ready for {@link parseLargeBlobPayload}, or null
 *
 * @example
 * ```typescript
 * const payload = await fetchAssertionPayload(bufferToBase64(response.clientDataJSON));
 * if (payload) {
 *   const { jweToken, verifyPayload } = parseLargeBlobPayload(payload);
 * }
 * ```
 */
export async function fetchAssertionPayload(
  clientDataJSON: string,
  options: LoopbackOptions = {}
): Promise<string | null> {
  if (!clientDataJSON) return null;

  if (options.debug) {
    console.log('[FIDO2-JS] largeBlob absent — requesting the JWE over the loopback /payload route');
  }

  const reply = await sendLoopbackFrame('/payload', { clientDataJSON }, options);

  if (!reply) return null;

  if (reply.status !== 'ok') {
    // "no payload for that assertion" is the expected miss: this transport did deliver a blob, or
    // the payload was already taken, or it aged out.
    const rawReason = typeof reply.message === 'string' ? reply.message : '';
    const safeReason =
      rawReason.replace(/[\x00-\x1F\x7F]+/g, ' ').replace(/\s+/g, ' ').trim() || 'unknown reason';
    const logSafeReason = JSON.stringify(safeReason) ?? '"unknown reason"';
    console.warn('[FIDO2-JS] loopback /payload declined:', logSafeReason);
    return null;
  }

  const payload = reply.payload;

  if (payload == null) {
    console.warn('[FIDO2-JS] loopback /payload returned ok with no payload field');
    return null;
  }

  // The service parses the payload before replying, so it arrives as a JSON *object*.
  // parseLargeBlobPayload takes the textual form, matching what largeBlob delivers.
  const asText = typeof payload === 'string' ? payload : JSON.stringify(payload);

  if (options.debug) {
    const safeLength = Number.isFinite(Number(asText.length)) ? Number(asText.length) : 0;
    console.log(`[FIDO2-JS] loopback /payload returned ${safeLength} chars`);
  }

  return asText;
}
